/** W82 Design-Web-1 (D-1661, DWW-1): die Eröffnung der Startseite nach dem iOS-Entwurf (d07b42f7, `BuehneEroeffnung.swift`,
 *  `BuehneEroeffnungAnsicht.swift`, design-neu-ios.md „1 Auftritt und Startseite“), auf den Bestand der Web-Startseite gelegt.
 *  Erstes Öffnen des Tages, lebhaft, Überall (darf „rundflug“); sie ersetzt dort den Rundflug.
 *
 *  Raum (0 bis 5 s): ferne Briefe ziehen klein, unscharf, in der Farbe des Grunds hinter dem Inhalt durch. Ankunft (0,06 bis
 *  1,9 s): bis acht Briefe im Bogen von den Seiten und von unten auf den Haufen, wo die Kacheln entstehen, alle 115 ms, Neigung in
 *  Flugrichtung, Flachlegen mit Nachfedern. Nah (0,56 s): ein naher Brief groß und unscharf vorbei. Licht hinter dem Haufen
 *  (1,45 s), der Gaist steigt im Bogen auf seinen Platz (1,76 s); seine Augen folgen Haufen, Heldbrief, Funke, jedem sortierten
 *  Brief und am Ende dir. Fang des Heldbriefs (erster Brief „Zu prüfen“; Ausholen, Vorschnellen, Aufprall, Rückfedern), Öffnen
 *  (umdrehen, Lasche, Bogen hebt sich), Funke (Leuchtstift über die Belegstelle, Blitz mit Strahlen, Lichtwelle), Kacheln entstehen,
 *  wo die Welle sie erreicht; Ordnung (Heldbrief in „Zu prüfen“, dann der Haufen von oben, alle 100 ms ein Brief in seine Kachel,
 *  Anstoßen, Zahl rollt); Module schweben ein; Ruhe (Hüpfer, Landung, Kopfblase wächst aus ihrer Ecke).
 *
 *  Technik (R-4): nur transform und opacity über die Web Animations API; die Briefe liegen in zwei Ebenen (vorn über dem Inhalt
 *  unter Kopf- und Reiterleiste, hinten zwischen Grund und Inhalt), der Gaist ist die echte Platzfigur (keine zweite Figur, sie
 *  übernimmt im selben Bild). Die Bühnenuhr (iOS `BuehneUhr`) führt alle Animationen: hängt ein Bild länger als 100 ms, hält der
 *  Plan an, statt Takte zu überspringen (bis zum Vorrat `AUSGLEICH_S`). Tipp, Klick, Taste, Rollen, Rad oder Wischen schneiden
 *  sofort in die Endlage.
 *  Prüfhilfe (nur Vorschau-Bau): `?eroeffnung=<ms>` hält die Eröffnung an der Zeit an. */
import { BUEHNE, EXPO, SANFT, bogen2, bogen3, federS, glatt, klemme, kubisch, mix, puls, stoss, zufall, type FederS, type P } from "./feder";
import { briefAus, briefBauen, dunkelVon, glanzVon, koerperTf, masse, schattenTf, type Brief, type BriefDaten } from "./papierbrief";

export type Zelle = "ueberfaellig" | "bald" | "pruefen" | "fixkosten";
export const ZELLEN: Zelle[] = ["ueberfaellig", "bald", "pruefen", "fixkosten"];

export type EroeffnungDaten = {
  /** Zahl je Kachel (Fixkosten: Betrag, nur über null zählt) */
  anzahl: Record<Zelle, number>;
  /** Absender je Kachel, soweit die Startseite sie kennt */
  namen: Partial<Record<Zelle, string[]>>;
  /** Absender aus der eigenen Post für die fernen Briefe */
  fern: string[];
  /** Heldbrief aus dem ersten Vorschlag „Zu prüfen“; ohne Vorschlag kein Heldbrief */
  held: { name: string; betreff: string; zeile: string } | null;
};

export type EroeffnungEreignisse = {
  /** der erste Brief ist in der Kachel gelandet (ohne Brief: die Welle hat sie erreicht); ihre Zahl darf rollen */
  landung: (z: Zelle) => void;
  /** die Module darunter schweben ein */
  module: () => void;
  /** die Kopfblase wächst aus ihrer Ecke */
  blase: () => void;
  /** Ende (auch nach dem Schnitt, dann mit der Art des Ereignisses): Platzfigur, Kacheln und Module stehen im Bestand */
  ende: (geschnitten: boolean, art?: string) => void;
};

export type Ablauf = { schneiden: (art?: string) => void; abbrechen: () => void };

/** Vorrat des Stillstand-Ausgleichs je Eröffnung (s). Ein Hänger bis dahin hält den Plan an wie iOS `BuehneUhr` (Prüffall 900 ms);
 *  liefert der Browser den Bildtakt dauerhaft gedrosselt (Stromsparen, langsames Gerät, kopfloses WebKit: Bilder alle 100 bis
 *  140 ms), zählt danach die Wanduhr, sonst dauerte die Eröffnung dort ein Vielfaches ihres Plans und hielte Module und Kopfblase
 *  so lange fest. So steht sie höchstens um diesen Vorrat länger als ihr Plan; eine verborgene Seite zählt nicht dagegen.
 *  Grenze (Web-11b W11B-2, A-4): ein Hänger wird nur um den Rest des Vorrats ausgeglichen; Stockungen über 100 ms davor (Laden,
 *  Last) zehren daran, danach springt der Plan um den Rest des Hängers vor. */
const AUSGLEICH_S = 1;

/** Ferne Briefe am Desktop (W82 Web-11b W11B-1, F-W11-1 Fassung A; N3 D-1791: zwei statt drei, weil die weiche Kante der Welle
 *  einen Zeichenschritt kostet): Bahnen aus der Liste der fernen Briefe (Index), die nahe beieinander ziehen. Der Browser zeichnet
 *  je Bild das Rechteck um alles, was sich bewegt, neu; sechs Briefe über die ganze Fläche machen es im Mittel 60 % des Fensters
 *  groß, die Bahnen 0, 4, 5 11 %, diese zwei 2 % (kleinste aller Zweierwahlen), und im Zeichnen ohne Grafikkarte kam jedes
 *  neunte Bild zu spät. */
const FERN_DESKTOP = [0, 5];

/** Endmaßstab der Lichtwelle (Größe `groesse` um `mitte`): mobil bis über die Fensterdiagonale (iOS BuehneWelleBild); am Desktop
 *  (W82 Web-11b W11B-1, F-W11-1 Fassung A) bis über die fernste Ecke des Rahmens um Gaist und Kachelfläche `kacheln` hinaus, mit
 *  einem Zuschlag von einem Fünftel, nie über die Fensterdiagonale. Verlauf, Dauer und Deckkraft bleiben. */
export function welleEnde(W: number, H: number, groesse: number, mitte: [number, number], kacheln: { links: number; rechts: number; oben: number; unten: number } | null): number {
  const fenster = (Math.hypot(W, H) / groesse) * 1.2;
  if (!kacheln) return fenster;
  const dx = Math.max(Math.abs(kacheln.links - mitte[0]), Math.abs(kacheln.rechts - mitte[0]));
  const dy = Math.max(Math.abs(kacheln.oben - mitte[1]), Math.abs(kacheln.unten - mitte[1]));
  return Math.min(fenster, ((2 * Math.hypot(dx, dy)) / groesse) * 1.2);
}

/** Ein Takt der Bühnenuhr (iOS `BuehneUhr`): aus dem Abstand `dt` (s) seit dem letzten Bild der Schritt des Plans. Ein Bild über
 *  100 ms zählt als ein Bild, der Plan hält an statt Takte zu überspringen. War die Seite verborgen, hält er ganz an; sonst aus dem
 *  Vorrat, danach zählt die Wanduhr (gedrosselter Bildtakt, siehe AUSGLEICH_S). `verborgen` bleibt gesetzt, solange die Seite
 *  verborgen ist (R1 Design-Web-1 F-2: liefert der Browser auch verborgen Bilder, zählt keines davon gegen den Vorrat). */
export function uhrTakt(dt: number, vorrat: number, verborgen: boolean, sichtbar: boolean): { dt: number; vorrat: number; verborgen: boolean } {
  let schritt = dt;
  let rest = vorrat;
  if (dt > 0.1) {
    const ueber = dt - 1 / 60;
    if (verborgen) schritt = 1 / 60;
    else if (ueber <= rest) {
      rest -= ueber;
      schritt = 1 / 60;
    } else {
      schritt -= rest;
      rest = 0;
    }
  }
  return { dt: schritt, vorrat: rest, verborgen: verborgen && !sichtbar };
}

// ---------------------------------------------------------------- Takte (s), wie BuehneEroeffnungPlan
const T_ANKUNFT = 0.06;
const T_VERSATZ = 0.115;
const T_LICHT = 1.45;
const T_STEIGEN = 1.76;
const T_HELD = 1.98;
const SORT_VORLAUF = 0.07;
const HELD_EIN = federS(0.85, 0.72);
const H_ROT = federS(1, 1.3);
const H_MASS = federS(1, 1.2);
const AUF = federS(1, 0.38);
const STEIG_MASS = federS(1, 0.7);
const KACHEL = federS(0.63, 0.43);
const WEG_WELLE = kubisch(0.2, 0.7, 0.3, 1);

/** Zeiten des Plans, die nur an der Zahl der Briefe hängen (Modul-Auftritt der Startseite braucht sie vor dem Messen). */
export function eroeffnungTakte(haufen: number, mitHeld: boolean) {
  const tI = T_HELD + BUEHNE.fang.erreicht;
  const tMark = tI + 0.12 + 0.29 + 0.23 + 0.27;
  const tFunke = mitHeld ? tMark + 0.04 : T_STEIGEN + 0.9;
  const tHeldEin = tMark + 0.42;
  const tHeldLand = tHeldEin + HELD_EIN.erreicht;
  const tSortieren = mitHeld ? tHeldEin + 0.23 : tFunke + 0.5;
  const tLand = (j: number) => tSortieren + j * 0.1 + SORT_VORLAUF + BUEHNE.einordnen.erreicht;
  const tLetzte = Math.max(haufen ? tLand(haufen - 1) : tSortieren, mitHeld ? tHeldLand : tSortieren);
  const tHuepfen = tLetzte + 0.04;
  const tBlase = tLetzte + 0.11;
  const tModule = Math.max(tSortieren, tLetzte - 0.25);
  const tEnde = Math.max(tBlase + 0.76, tHuepfen + 0.56 + BUEHNE.landen.dauer * 0.7, tLetzte + 0.9);
  return { tI, tMark, tFunke, tHeldEin, tHeldLand, tSortieren, tLetzte, tHuepfen, tBlase, tModule, tEnde };
}

/** Welche Briefe auf den Haufen kommen (wie BuehneDatenSammler): je Kachel höchstens zwei, Fixkosten drei, mindestens fünf,
 *  mit dem Heldbrief zusammen höchstens acht; die Briefe zu „Überfällig“ liegen unten und landen zuletzt. */
export function haufenWahl(d: EroeffnungDaten): { ziel: Zelle; brief: BriefDaten }[] {
  const mitHeld = d.held !== null;
  const nUeber = Math.min(d.anzahl.ueberfaellig, 2);
  const nBald = Math.min(d.anzahl.bald, 2);
  const nPruefen = Math.max(0, Math.min(d.anzahl.pruefen, 2) - (mitHeld ? 1 : 0));
  let nFix = d.anzahl.fixkosten > 0 ? 3 : 0;
  if (nFix > 0) nFix = Math.max(nFix, 5 - (nUeber + nBald + nPruefen));
  const unten: { ziel: Zelle; brief: BriefDaten }[] = [];
  for (let j = 0; j < nUeber; j++) unten.push({ ziel: "ueberfaellig", brief: briefAus(d.namen.ueberfaellig?.[j], "ueberfaellig", j, j === 0 ? "Zahlungserinnerung" : undefined) });
  const rest: { ziel: Zelle; brief: BriefDaten }[] = [];
  for (let j = 0; j < nBald; j++) rest.push({ ziel: "bald", brief: briefAus(d.namen.bald?.[j], "bald", j) });
  for (let j = 0; j < nPruefen; j++) rest.push({ ziel: "pruefen", brief: briefAus(d.namen.pruefen?.[j], "pruefen", j) });
  for (let j = 0; j < Math.min(nFix, 8); j++) rest.push({ ziel: "fixkosten", brief: briefAus(d.namen.fixkosten?.[j], "fixkosten", j) });
  const r = zufall(1031);
  for (let i = rest.length - 1; i > 0; i--) {
    const k = Math.floor(r() * (i + 1)) % (i + 1);
    const a = rest[i];
    const b = rest[k];
    if (a && b) {
      rest[i] = b;
      rest[k] = a;
    }
  }
  return [...unten, ...rest].slice(0, mitHeld ? 7 : 8);
}

// ---------------------------------------------------------------- Bühnenuhr und Spieler
type Spur = { a: Animation; t0: number };

/** Prüfhilfe: Zeitpunkt aus `?eroeffnung=ms` (nur im Vorschau-Bau). */
function haltBei(): number | null {
  if (!import.meta.env.VITE_W82_VORSCHAU || typeof location === "undefined") return null;
  const m = /[?&]eroeffnung=(\d+)/.exec(location.search);
  return m ? Number(m[1]) / 1000 : null;
}

const hz50 = 0.02;
/** Tastet `zustand(t)` im Fenster [t0, t1] (s) ab und legt je Ziel eine angehaltene Animation an (Zeit führt die Bühnenuhr). */
function spur(spuren: Spur[], t0: number, t1: number, schritt: number, zustand: (t: number) => Record<string, Keyframe>, ziele: Record<string, Element | null | undefined>) {
  const n = Math.max(2, Math.ceil((t1 - t0) / schritt));
  const bilder: Record<string, Keyframe[]> = {};
  for (const k of Object.keys(ziele)) bilder[k] = [];
  for (let i = 0; i <= n; i++) {
    const t = t0 + ((t1 - t0) * i) / n;
    const w = zustand(t);
    for (const k of Object.keys(ziele)) {
      const b = w[k];
      if (b) bilder[k]?.push({ ...b, offset: i / n });
    }
  }
  for (const [k, el] of Object.entries(ziele)) {
    const b = bilder[k];
    if (!el || !b?.length || typeof (el as HTMLElement).animate !== "function") continue;
    const a = (el as HTMLElement).animate(b, { duration: Math.max(1, (t1 - t0) * 1000), fill: "both", easing: "linear" });
    a.pause();
    spuren.push({ a, t0 });
  }
}

/** Eine einfache Animation mit Kurve (Blenden, Strahlen, Module) unter der Bühnenuhr. */
function einfach(spuren: Spur[], el: Element | null | undefined, bilder: Keyframe[], t0: number, dauer: number, easing: string) {
  if (!el || typeof (el as HTMLElement).animate !== "function") return;
  const a = (el as HTMLElement).animate(bilder, { duration: dauer * 1000, fill: "both", easing });
  a.pause();
  spuren.push({ a, t0 });
}

type R = { x: number; y: number; w: number; h: number; oben: number; unten: number; links: number; rechts: number };
const rechteck = (el: Element): R => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width, h: r.height, oben: r.top, unten: r.bottom, links: r.left, rechts: r.right };
};

/** Blick von `von` nach `nach` als Richtung (iOS `gaistBlick`), kurz vor der Figur abgeschwächt. */
function blickRichtung(von: P, nach: P, g: number): P {
  const dx = nach[0] - von[0];
  const dy = nach[1] - von[1];
  const l = Math.max(1, Math.hypot(dx, dy));
  const k = Math.min(1, l / (g * 0.9));
  return [(dx / l) * k, (dy / l) * k];
}

/** Startet die Eröffnung auf der Startseite `wurzel` (`.startseite`). Die Seite hält Kacheln, Platzfigur, Module und Kopfblase
 *  bis zu den Ereignissen im ersten Bild (buehne.css `[data-eroeffnung]`). */
export function eroeffnungStarten(wurzel: HTMLElement, daten: EroeffnungDaten, ereignis: EroeffnungEreignisse): Ablauf {
  const spuren: Spur[] = [];
  const ebenen: HTMLElement[] = [];
  const zusatz: HTMLElement[] = [];
  let vorbei = false;
  let rahmen = 0;
  let zeit = 0;
  let letzte: number | null = null;
  // Vorrat des Stillstand-Ausgleichs (s) und ob die Seite seit dem letzten Bild verborgen war (Takt unten)
  let vorrat = AUSGLEICH_S;
  let verborgen = false;
  const anstehend: { t: number; fn: () => void }[] = [];
  const startY = scrollY;

  const aufraeumen = () => {
    vorbei = true;
    // im selben Bild wie das Abbrechen der Animationen: kein Halten mehr, kein neuer Auftritt des Bestands (buehne.css)
    wurzel.dataset.eroeffnung = "fertig";
    cancelAnimationFrame(rahmen);
    for (const s of spuren) s.a.cancel();
    for (const e of ebenen) e.remove();
    for (const e of zusatz) e.remove();
    abmelden();
  };
  const schneiden = (art?: string) => {
    if (vorbei) return;
    aufraeumen();
    // die Seite stellt Zahlen, Kopfblase und Module selbst in die Endlage (ohne Bewegung, Unterbrechen-Regel)
    anstehend.splice(0);
    ereignis.ende(true, art);
  };
  const abbrechen = () => {
    if (!vorbei) aufraeumen();
  };
  const endeNormal = () => {
    if (vorbei) return;
    aufraeumen();
    ereignis.ende(false);
  };
  const aufSchnitt = (e: Event) => {
    if (e.type === "scroll" && Math.abs(scrollY - startY) < 2) return;
    schneiden(e.type);
  };
  const ARTEN = ["click", "keydown", "wheel", "touchmove", "scroll"] as const;
  const aufSicht = () => {
    if (document.visibilityState === "hidden") verborgen = true;
  };
  // Breite oder Lage des Fensters ändern sich (Fenster gezogen, Gerät gedreht): die Wege sind für die alte Breite gerechnet,
  // darum Endlage wie nach einem Tipp (eine reine Höhenänderung, etwa die Leiste von Safari beim Rollen, schneidet nicht)
  const aufGroesse = () => {
    if (Math.abs(innerWidth - W) > 1) schneiden("resize");
  };
  const anmelden = () => {
    for (const art of ARTEN) addEventListener(art, aufSchnitt, { capture: true, passive: true });
    document.addEventListener("visibilitychange", aufSicht);
    addEventListener("resize", aufGroesse, { passive: true });
  };
  const abmelden = () => {
    for (const art of ARTEN) removeEventListener(art, aufSchnitt, { capture: true });
    document.removeEventListener("visibilitychange", aufSicht);
    removeEventListener("resize", aufGroesse);
  };

  // ---------------------------------------------------------------- Lage messen
  const kopf = wurzel.querySelector<HTMLElement>(".startseite-kopf");
  const figur = kopf?.querySelector<HTMLElement>(".geist-platz > .geist-figur") ?? null;
  const kacheln: Partial<Record<Zelle, HTMLElement>> = {};
  for (const z of ZELLEN) {
    const k = kopf?.querySelector<HTMLElement>(`.lichtkachel[data-zelle="${z}"]`);
    if (k) kacheln[z] = k;
  }
  const block = kopf?.querySelector<HTMLElement>(".lichtkacheln") ?? null;
  const W = innerWidth;
  const H = innerHeight;
  const leiste = wurzel.querySelector("header.leiste");
  const oben = leiste ? Math.max(0, leiste.getBoundingClientRect().bottom) : 0;
  if (!figur || !block || !kacheln.ueberfaellig) {
    schneiden();
    return { schneiden, abbrechen };
  }
  const gr = rechteck(figur);
  const kb = rechteck(block);
  // liegen Kacheln oder Gaist nicht ganz im Bild (große Schrift, niedriges Fenster, quer), flöge die Post ins Leere: Endlage
  if (kb.oben < oben || kb.unten > H - 8 || gr.oben < oben - gr.h * 0.2 || gr.unten > H) {
    schneiden();
    return { schneiden, abbrechen };
  }
  const mobil = W < 744;
  const g = gr.w;
  const gf = g / 124;
  const heim: P = [gr.x, gr.y];
  const blaseEl = kopf?.querySelector('.blase[data-form="kopf"]');
  const L = mobil ? klemme(W * 0.33, 108, 136) : klemme(kb.w * 0.14, 132, 168);
  const heldBasis = L * (H < 740 ? 1.34 : 1.48);
  const [hmW, hmH] = masse("c5", heldBasis);
  const fang: P = (() => {
    if (!blaseEl) return [heim[0] + g * 1.25, heim[1]];
    const b = rechteck(blaseEl);
    return [Math.min(b.x, b.links + hmW * 0.5 + 24), b.y];
  })();
  const lageZ: Partial<Record<Zelle, R>> = {};
  for (const z of ZELLEN) {
    const k = kacheln[z];
    if (k) lageZ[z] = rechteck(k);
  }
  const haufenMitte: P = [kb.x, kb.y];
  const mitte = (z: Zelle): P => {
    const r = lageZ[z];
    return r ? [r.x, r.y] : haufenMitte;
  };
  const r = zufall(20261031);
  const richtung = fang[0] >= heim[0] ? 1 : -1;
  const auswahl = haufenWahl(daten);
  const held = daten.held;
  const tk = eroeffnungTakte(auswahl.length, held !== null);
  const { tI, tMark, tFunke, tHeldEin, tHeldLand, tSortieren, tHuepfen, tBlase, tModule, tEnde } = tk;
  const tDrehen = tI + 0.12;
  const tLasche = tDrehen + 0.29;
  const tBogen = tLasche + 0.23;
  const welleBei = (p: P) => tFunke + 0.06 + Math.hypot(p[0] - heim[0], p[1] - heim[1]) / 620;

  // Ebenen: vorn über dem Inhalt am body (unter der Reiterleiste; der Plan hält die Bögen unter der Kopfleiste, `oben`), hinten in der
  // Startseite zwischen Grund und Inhalt (buehne.css)
  const vorn = document.createElement("div");
  vorn.className = "bue-flugebene";
  vorn.setAttribute("aria-hidden", "true");
  const hinten = document.createElement("div");
  hinten.className = "bue-fernebene";
  hinten.setAttribute("aria-hidden", "true");
  wurzel.prepend(hinten);
  document.body.append(vorn);
  ebenen.push(vorn, hinten);

  // ---------------------------------------------------------------- Haufen: Anflug, Liegen, Welle, Einordnen
  type HB = {
    b: Brief; ziel: Zelle; E: P; S: P; C: P; tA: number; pf: FederS; sf: FederS; rf: FederS; tFlach: number;
    rx0: number; ry0: number; rz0: number; rzE: number; s0: number; ph: number; ryE: number;
    tS: number; tLand: number; tW: number; sortWeg: (p: number) => P; wr: P; weg: (p: number) => P;
  };
  const seiten = [-1, 1, -1, 1, 0.55, -0.55, 1, -1];
  const haufen: HB[] = auswahl.map(({ ziel, brief }, i) => {
    const E: P = [haufenMitte[0] + r.gauss() * L * 0.27, haufenMitte[1] + r.gauss() * L * 0.16 - i * 1.6];
    const seite = seiten[i % seiten.length] ?? 1;
    const winkel = Math.abs(seite) === 0.55 ? (seite > 0 ? 62 : 118) : seite > 0 ? -6 + r() * 34 : 186 - r() * 34;
    const a = (winkel * Math.PI) / 180;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    const rand = L * 0.9;
    const bisX = dx > 0 ? (W + rand - E[0]) / dx : dx < 0 ? (-rand - E[0]) / dx : Infinity;
    const bisY = dy > 0 ? (H + rand - E[1]) / dy : dy < 0 ? (-rand - E[1]) / dy : Infinity;
    const ferne = Math.max(Math.min(bisX, bisY), Math.hypot(W, H) * 0.3);
    const S: P = [E[0] + dx * ferne, E[1] + dy * ferne];
    const mSE: P = [(S[0] + E[0]) / 2, (S[1] + E[1]) / 2];
    const dist = Math.hypot(E[0] - S[0], E[1] - S[1]);
    const C: P = [mSE[0] + (r() - 0.5) * dist * 0.16, Math.max(oben + 40, Math.min(mSE[1], E[1]) - dist * (0.26 + r() * 0.1))];
    const antwort = 1.05 + r() * 0.16;
    const pf = federS(0.72, antwort);
    const tA = T_ANKUNFT + i * T_VERSATZ;
    return {
      b: briefBauen(brief, L, vorn, "mitte"), ziel, E, S, C, tA, pf, sf: federS(1, antwort * 0.9), rf: federS(1, antwort * 0.8),
      tFlach: tA + pf.erreicht * 0.74,
      rx0: -8 - r() * 22, ry0: (r() - 0.5) * 50, rz0: (r() - 0.5) * 120, rzE: (r() - 0.5) * 56, s0: 1.22 + r() * 0.22, ph: r() * 6.28, ryE: (r() - 0.5) * 6,
      tS: 0, tLand: 0, tW: 0, sortWeg: () => E, wr: [0, 0], weg: bogen2(S, C, E, 0.42),
    };
  });
  const sortF = BUEHNE.einordnen;
  haufen.slice().reverse().forEach((h, j) => {
    h.tS = tSortieren + j * 0.1;
    h.tLand = h.tS + SORT_VORLAUF + sortF.erreicht;
    h.tW = welleBei(h.E);
    const Z = mitte(h.ziel);
    const hebe = L * 0.42;
    h.sortWeg = bogen3(h.E, [h.E[0], h.E[1] - hebe], [Z[0], Z[1] - hebe * 0.9], Z, 0.3);
    const dx = h.E[0] - heim[0];
    const dy = h.E[1] - heim[1];
    const l = Math.max(1, Math.hypot(dx, dy));
    h.wr = [dx / l, dy / l];
  });
  type Lage = { x: number; y: number; rx: number; ry: number; rz: number; s: number; o: number; hoehe: number; glanzPlus: number };
  const haufenLage = (h: HB, t: number): Lage => {
    const w = stoss(t - h.tW, 0.15);
    let z: Lage;
    if (t < h.tS) {
      const lt = t - h.tA;
      const p = h.pf(lt);
      const q = h.weg(p);
      const vx = (h.weg(h.pf(lt + 0.01))[0] - h.weg(h.pf(lt - 0.01))[0]) / 20;
      const luft = 1 - klemme(p, 0, 1);
      const fl = BUEHNE.flachlegen(t - h.tFlach);
      z = {
        x: q[0], y: q[1],
        rx: mix(h.rx0, 34, fl) + 15 * Math.sin((lt * 1000) / 64 + h.ph) * luft * luft,
        ry: mix(h.ry0, h.ryE, glatt(p)) + 12 * Math.sin((lt * 1000) / 83 + h.ph * 1.7) * luft * luft,
        rz: mix(h.rz0, h.rzE, h.rf(lt)) + klemme(vx * 9, -15, 15) * (1 - klemme(fl, 0, 1) * 0.7),
        s: mix(h.s0, 1, h.sf(lt)), o: 1, hoehe: klemme(Math.max(luft, 1 - fl), 0, 1), glanzPlus: 0,
      };
    } else {
      const lt = t - h.tS;
      const dip = lt > 0 && lt < SORT_VORLAUF + 0.02 ? Math.sin((Math.PI * lt) / (SORT_VORLAUF + 0.02)) : 0;
      const p = sortF(lt - SORT_VORLAUF);
      const q = h.sortWeg(p);
      const vx = (h.sortWeg(sortF(lt - SORT_VORLAUF + 0.01))[0] - h.sortWeg(sortF(lt - SORT_VORLAUF - 0.01))[0]) / 20;
      const auf = AUF(lt - 0.03);
      const pp = klemme(p, 0, 1);
      z = {
        x: q[0], y: q[1] + dip * 3,
        rx: mix(34, -4, auf), ry: mix(h.ryE, 0, auf),
        rz: mix(h.rzE, 0, glatt(p)) + klemme(vx * 7, -12, 12),
        s: (1 - dip * 0.03) * (pp < 0.22 ? mix(1, 1.07, pp / 0.22) : mix(1.07, 0.3, glatt((pp - 0.22) / 0.78))),
        o: 1 - glatt((t - (h.tLand - 0.15)) / 0.15), hoehe: klemme(auf, 0, 1) * (1 - pp * 0.6), glanzPlus: 0,
      };
    }
    z.x += h.wr[0] * w * 5;
    z.y += h.wr[1] * w * 5;
    z.glanzPlus = w * 0.32;
    return z;
  };
  const briefBild = (b: Brief, z: Lage, stark = 1): Record<string, Keyframe> => {
    const sx = (1 + z.hoehe * 0.28) * z.s;
    const sy = sx * Math.max(0.55, Math.cos((z.rx * Math.PI) / 180));
    return {
      k: { transform: koerperTf(z.x, z.y, b.w, b.h, z.rx, z.ry, z.rz, z.s), opacity: z.o },
      s: { transform: schattenTf(z.x + 2 + z.hoehe * 10, z.y + 4 + z.hoehe * 26, b.w * 0.78, b.h * 0.7, z.rz, sx, sy), opacity: z.o * mix(0.5, 0.12, z.hoehe) * stark },
      g: { opacity: klemme(glanzVon(z.rx, z.ry) + z.glanzPlus, 0, 0.75) },
      d: { opacity: dunkelVon(z.rx, z.ry) },
    };
  };
  for (const h of haufen) {
    spur(spuren, h.tA - 0.02, h.tLand + 0.04, hz50, (t) => briefBild(h.b, haufenLage(h, t)), { k: h.b.koerper, s: h.b.schatten, g: h.b.glanz, d: h.b.dunkel });
  }

  // ---------------------------------------------------------------- Heldbrief: Anflug von rechts, Fang, Öffnen, Lesen, Einordnen
  let heldPos: ((t: number) => P) | null = null;
  if (held) {
    const hb = briefBauen({ ...briefAus(held.name, "pruefen", 0), form: "c5" }, heldBasis, vorn, "mitte", { kopf: held.name, betreff: held.betreff, zeile: held.zeile });
    const kontakt: P = [heim[0] + richtung * (g * 0.31 + 13 * gf + hmW * 0.5 + 2), heim[1] - g * 0.04];
    const hS: P = [W + hmW * 0.9, Math.max(oben + hmH * 0.75, kontakt[1] - H * 0.16)];
    const hC: P = [mix(hS[0], kontakt[0], 0.45), Math.min(hS[1], kontakt[1]) - Math.max(48, H * 0.07)];
    const heldWeg = bogen2(hS, hC, kontakt, 0.45);
    const lese: P = [Math.min(fang[0], W - hmW * 0.5 - 10), Math.max(fang[1], oben + 8 + 0.93 * hmH)];
    const schweb = (t: number) => (t > tI ? Math.sin((t - tI) / 0.42) * 2.4 * klemme((t - tI) / 0.4, 0, 1) : 0);
    const lesen = (t: number) => BUEHNE.uebergabe(t - (tI + 0.11));
    const heldOrt = (t: number): P => {
      const q = heldWeg(BUEHNE.fang(t - T_HELD));
      const l = lesen(t);
      return [q[0] + (lese[0] - kontakt[0]) * l, q[1] + (lese[1] - kontakt[1]) * l + schweb(t)];
    };
    const ziel = mitte("pruefen");
    const start = heldOrt(tHeldEin);
    const heldEinWeg = bogen3(start, [start[0], start[1] - hmH * 0.5], [ziel[0], ziel[1] - hmH * 0.6], ziel, 0.3);
    const heldLage = (t: number): Lage => {
      const lt = t - T_HELD;
      const dreh = 180 * BUEHNE.umdrehen(t - tDrehen);
      if (t < tHeldEin) {
        const p = BUEHNE.fang(lt);
        const q = heldOrt(t);
        const vx = (heldWeg(BUEHNE.fang(lt + 0.01))[0] - heldWeg(BUEHNE.fang(lt - 0.01))[0]) / 20;
        const luft = 1 - klemme(p, 0, 1);
        return {
          x: q[0], y: q[1], rx: 16 * Math.sin((lt * 1000) / 70) * luft * luft, ry: dreh + 10 * Math.sin((lt * 1000) / 90 + 1) * luft * luft,
          rz: mix(38, -3, H_ROT(lt)) + klemme(vx * 8, -14, 14), s: mix(1.28, 1, H_MASS(lt)), o: 1, hoehe: 1, glanzPlus: 0,
        };
      }
      const et = t - tHeldEin;
      const p = HELD_EIN(et);
      const q = heldEinWeg(p);
      const vx = (heldEinWeg(HELD_EIN(et + 0.01))[0] - heldEinWeg(HELD_EIN(et - 0.01))[0]) / 20;
      const pp = klemme(p, 0, 1);
      return {
        x: q[0], y: q[1], rx: 0, ry: dreh, rz: -3 * (1 - pp) + klemme(vx * 6, -10, 10),
        s: pp < 0.2 ? mix(1, 1.05, pp / 0.2) : mix(1.05, 0.28, glatt((pp - 0.2) / 0.8)),
        o: 1 - glatt((t - (tHeldLand - 0.15)) / 0.15), hoehe: 1 - pp * 0.6, glanzPlus: 0,
      };
    };
    heldPos = (t) => {
      const z = heldLage(t);
      return [z.x, z.y];
    };
    const lasche = (t: number) => 180 * BUEHNE.lasche(t - tLasche);
    const bogen = (t: number) => EXPO(klemme((t - tBogen) / 0.46, 0, 1));
    const mark = (t: number) => EXPO(klemme((t - tMark) / 0.38, 0, 1));
    const t0 = T_HELD - 0.02;
    const t1 = tHeldLand + 0.05;
    const persp = Math.round(hmW * 2.4);
    spur(spuren, t0, t1, hz50, (t) => {
      const z = heldLage(t);
      const breit = Math.max(0.3, Math.abs(Math.cos((z.ry * Math.PI) / 180)));
      const bild = briefBild(hb, { ...z, rx: z.rx * 0.5, s: z.s * breit }, 0.4);
      const w = lasche(t);
      return {
        k: { transform: koerperTf(z.x, z.y, hb.w, hb.h, z.rx, z.ry, z.rz, z.s), opacity: z.o },
        s: bild.s ?? {},
        g: { opacity: klemme(glanzVon(z.rx, z.ry), 0, 0.75) },
        d: { opacity: dunkelVon(z.rx, z.ry) },
        lz: { transform: `perspective(${persp}px) rotateX(${Math.min(w, 90).toFixed(2)}deg)`, opacity: w < 90 ? 1 : 0 },
        la: { transform: `perspective(${persp}px) rotateX(${Math.max(w, 90).toFixed(2)}deg)`, opacity: w >= 90 ? 1 : 0 },
        bo: { transform: `translateY(${(-hmH * 0.9 * 0.56 * bogen(t)).toFixed(2)}px)` },
        m: { transform: `scaleX(${mark(t).toFixed(4)})` },
      };
    }, { k: hb.koerper, s: hb.schatten, g: hb.glanz, d: hb.dunkel, lz: hb.held?.lascheZu, la: hb.held?.lascheAuf, bo: hb.held?.bogen, m: hb.held?.mark });
  }

  // ---------------------------------------------------------------- Ferne Briefe (hinten) und der nahe Brief (vorn)
  const fernweg: [[number, number], [number, number], number][] = [
    [[-0.14, 0.16], [1.3, 0.04], 0.42], [[1.14, 0.34], [-0.3, 0.2], 0.5], [[-0.14, 0.9], [1.3, 0.68], 0.55],
    [[1.14, 1.0], [-0.3, 0.84], 0.46], [[0.12, -0.08], [-0.3, 0.52], 0.4], [[0.9, -0.08], [1.3, 0.58], 0.44],
  ];
  const fernStart = [0, 0.08, 0.18, 0.28, 0.42, 0.52];
  const fernZahl = mobil ? 4 : 6;
  for (let i = 0; i < fernZahl; i++) {
    const [s0, e0, s] = fernweg[i] ?? fernweg[0] ?? [[0, 0], [1, 0], 0.4];
    // waagerecht im Bild (die Ebene klippt am Fenster, die Briefe blenden am Anfang und Ende ihres Wegs ein und aus)
    const rand = L * s * 0.62;
    const imBild = (x: number) => klemme(x, rand, W - rand);
    const S: P = [imBild(s0[0] * W), s0[1] * H];
    const E: P = [imBild(e0[0] * W), e0[1] * H];
    const tA = fernStart[i] ?? 0;
    const dauer = 3.4 + (i % 4) * 0.45;
    const rz0 = (r() - 0.5) * 70;
    const rz1 = rz0 + (r() - 0.5) * 50;
    const rx = (r() - 0.5) * 30;
    const ry = (r() - 0.5) * 40;
    // W82 Web-11b W11B-1 (F-W11-1, Fassung A): am Desktop nur die Bahnen aus FERN_DESKTOP, mobil wie bisher die ersten vier; die
    // Zufallsfolge läuft für alle weiter, damit alles danach (naher Brief, Haufen) gleich bleibt
    if (!mobil && !FERN_DESKTOP.includes(i)) continue;
    const vorlage = briefAus(daten.fern[i] ?? null, "fern", i);
    const b = briefBauen(daten.fern[i] && i % 3 === 1 ? { ...vorlage, hinten: true } : vorlage, L, hinten, "fern");
    spur(spuren, tA, tA + dauer, 1 / 30, (t) => {
      const p = klemme((t - tA) / dauer, 0, 1);
      const q = SANFT(p);
      const x = mix(S[0], E[0], q);
      const y = mix(S[1], E[1], q) - Math.sin(Math.PI * q) * H * 0.05;
      const o = 0.5 * glatt(p / 0.1) * (1 - glatt((p - 0.85) / 0.15));
      return { k: { transform: koerperTf(x, y, b.w, b.h, rx, ry, mix(rz0, rz1, q), s), opacity: t < tA ? 0 : o } };
    }, { k: b.koerper });
  }
  {
    const b = briefBauen({ ...briefAus(null, "fern", 3), hinten: true, form: "dl" }, L, vorn, "nah");
    const S: P = [-0.45 * W, 0.98 * H];
    const E: P = [1.5 * W, 0.6 * H];
    const tA = 0.56;
    const dauer = 0.52;
    spur(spuren, tA - 0.02, tA + dauer + 0.02, hz50, (t) => {
      const p = SANFT(klemme((t - tA) / dauer, 0, 1));
      const x = mix(S[0], E[0], p);
      const y = mix(S[1], E[1], p) - Math.sin(Math.PI * p) * H * 0.07;
      const sichtbar = t >= tA && t <= tA + dauer;
      return { k: { transform: koerperTf(x, y, b.w, b.h, 18 - p * 30, -24 + p * 40, -28 + p * 34, 2.1), opacity: sichtbar ? 0.92 : 0 } };
    }, { k: b.koerper });
  }

  // ---------------------------------------------------------------- Der Gaist (echte Platzfigur): Licht, Aufsteigen, Blick, Fang, Funke, Hüpfer
  const sprung = figur.querySelector<HTMLElement>(".geist-sprung");
  const aura = figur.querySelector<HTMLElement>(".geist-aura");
  const boden = figur.querySelector<HTMLElement>(".geist-boden");
  const blickG = figur.querySelector<SVGElement>("svg.geist .blick");
  const augen = Array.from(figur.querySelectorAll<SVGElement>("svg.geist .auge"));
  const funke = figur.querySelector<SVGElement>("svg.geist .funke");
  const schein = figur.querySelector<SVGElement>("svg.geist .funke-schein");
  const unten: P = [haufenMitte[0], haufenMitte[1] + g * 0.15];
  const steigWeg = bogen2(unten, [haufenMitte[0] + (heim[0] - haufenMitte[0]) * 0.15, heim[1] - g * 0.55], heim, 0.5);
  const u: P = (() => {
    const ux = fang[0] - heim[0];
    const uy = fang[1] - heim[1];
    const ul = Math.max(1, Math.hypot(ux, uy));
    return [ux / ul, uy / ul];
  })();
  const huepf = (t: number) => {
    const lt = t - tHuepfen;
    const hub = 16 * gf;
    if (lt <= 0 || lt >= 0.56) return 0;
    return lt < 0.26 ? hub * EXPO(lt / 0.26) : hub * (1 - Math.pow((lt - 0.26) / 0.3, 2));
  };
  const fangVersatz = (t: number) => {
    if (!held) return 0;
    const t1 = tI - 0.24;
    const t2 = tI - 0.09;
    if (t < t1) return 0;
    if (t < t2) return -7 * gf * SANFT((t - t1) / (t2 - t1));
    if (t < tI) {
      const k = (t - t2) / (tI - t2);
      return mix(-7, 13, k * k * k) * gf;
    }
    return 13 * gf * (1 - BUEHNE.rueckfedern(t - tI));
  };
  const gaistLage = (t: number): { p: P; mass: number } => {
    let p: P;
    let s: number;
    if (t < T_STEIGEN) {
      p = [unten[0], unten[1]];
      s = 0.62;
    } else {
      p = steigWeg(BUEHNE.steigen(t - T_STEIGEN));
      s = mix(0.62, 1, STEIG_MASS(t - T_STEIGEN));
    }
    const a = fangVersatz(t);
    return { p: [p[0] + a * u[0], p[1] + a * u[1] - huepf(t)], mass: s };
  };
  // Blick: Folger mit 45 ms Zeitkonstante auf das, was gerade zählt; vorab je 8 ms gerechnet (iOS GaistBlickFolger)
  const sortierbriefBei = (t: number): HB | undefined => {
    let wahl: HB | undefined;
    for (let i = haufen.length - 1; i >= 0; i--) {
      const h = haufen[i];
      if (h && t >= h.tS - 0.04) wahl = h;
    }
    return wahl ?? haufen[haufen.length - 1];
  };
  const blickZiel = (t: number): P | null => {
    const m = gaistLage(t).p;
    const mm: P = [m[0], m[1] - g * 0.12];
    const auf = (p: P) => blickRichtung(mm, p, g);
    if (t < T_STEIGEN + 0.26) return null;
    const sortiert = (): P | null => {
      const h = sortierbriefBei(t);
      if (!h) return null;
      const z = haufenLage(h, Math.min(t, h.tLand - 0.02));
      return auf([z.x, z.y]);
    };
    if (!heldPos) {
      if (t < tFunke - 0.1) return auf(haufenMitte);
      if (t < tFunke + 0.34) return auf([mm[0] + g * richtung, mm[1] - g]);
      if (t < tHuepfen - 0.06) return sortiert();
      return t > tHuepfen ? [0, 0] : null;
    }
    if (t < T_HELD + 0.12) return auf(haufenMitte);
    if (t < tI + 0.12) return auf(heldPos(t));
    if (t < tFunke - 0.16) {
      const q = heldPos(t);
      return auf([q[0], q[1] - hmH * 0.3]);
    }
    if (t < tFunke + 0.34) return auf([mm[0] + g * richtung, mm[1] - g]);
    if (t < tHeldLand) return auf(heldPos(t));
    if (t < tHuepfen - 0.06) return sortiert();
    return t > tHuepfen ? [0, 0] : null;
  };
  const blickWerte: P[] = [];
  const blickVon = T_STEIGEN - 0.1;
  {
    let x = 0;
    let y = 0.18;
    for (let t = blickVon; t <= tHuepfen + 0.9; t += 0.008) {
      const z = blickZiel(t);
      const a = 1 - Math.exp(-0.008 / 0.045);
      x += ((z ? z[0] : 0) - x) * a;
      y += ((z ? z[1] : 0.18) - y) * a;
      blickWerte.push([x, y]);
    }
  }
  const blick = (t: number): P => blickWerte[Math.max(0, Math.min(blickWerte.length - 1, Math.round((t - blickVon) / 0.008)))] ?? [0, 0];
  const lid = (t: number) => {
    for (const tb of [T_STEIGEN + 0.62, tFunke + 0.9]) {
      const p = (t - tb) / 0.15;
      if (p > 0 && p < 1) return p < 0.45 ? 1 - 0.92 * SANFT(p / 0.45) : 0.08 + 0.92 * SANFT((p - 0.45) / 0.55);
    }
    return 1;
  };
  const leib = (t: number) => {
    let rz = 0;
    let ry = 0;
    let sx = 1;
    let sy = 1;
    const o = klemme((t - (T_STEIGEN - 0.06)) / 0.3, 0, 1);
    if (t >= T_STEIGEN && t < T_STEIGEN + BUEHNE.steigen.dauer) {
      const a = gaistLage(t - 0.01).p;
      const b = gaistLage(t + 0.01).p;
      const vx = (b[0] - a[0]) / 20;
      const vy = (b[1] - a[1]) / 20;
      const sp = klemme(Math.hypot(vx, vy) / 1.4, 0, 1);
      sx *= 1 - 0.08 * sp;
      sy *= 1 + 0.08 * sp;
      rz += klemme(vx * 8, -12, 12);
    }
    if (held) {
      const dreh = SANFT(klemme((t - (T_HELD + 0.14)) / 0.44, 0, 1)) * (1 - SANFT(klemme((t - (tFunke + 0.42)) / 0.48, 0, 1)));
      ry += 24 * dreh * richtung;
      const t1 = tI - 0.24;
      const t2 = tI - 0.09;
      if (t >= t1 && t < t2) {
        const k = SANFT((t - t1) / (t2 - t1));
        rz -= 8 * k * richtung;
        sx *= 1 + 0.05 * k;
        sy *= 1 - 0.05 * k;
      } else if (t >= t2 && t < tI) {
        const k = (t - t2) / (tI - t2);
        rz += mix(-8, 11, k) * richtung;
        sx *= mix(1.05, 0.93, k);
        sy *= mix(0.95, 1.07, k);
      } else if (t >= tI) {
        const f = BUEHNE.aufprall(t - tI);
        rz += 11 * (1 - f) * richtung;
        sx *= mix(0.88, 1, f);
        sy *= mix(1.08, 1, f);
      }
    }
    if (t > tFunke + 0.4 && t < tHuepfen + 0.9) rz += blick(t)[0] * 4 * (1 - glatt((t - tHuepfen - 0.3) / 0.5));
    const fp = puls(t - tFunke, 0.11, 0.52);
    sx *= 1 + 0.05 * fp;
    sy *= 1 + 0.05 * fp;
    const lt = t - tHuepfen;
    if (lt > 0 && lt < 0.26) {
      const k = Math.sin((Math.PI * lt) / 0.26);
      sx *= 1 - 0.05 * k;
      sy *= 1 + 0.06 * k;
    }
    if (lt >= 0.56) {
      const f = BUEHNE.landen(lt - 0.56);
      sx *= mix(1.06, 1, f);
      sy *= mix(0.92, 1, f);
    }
    return { rz, ry, sx, sy, o };
  };
  const auraLage = (t: number) => {
    const p = EXPO(klemme((t - T_LICHT) / 1.2, 0, 1));
    let m = mix(0.3, 1, p);
    const lf = (t - tFunke) / 1.0;
    if (lf > 0 && lf < 1) m *= lf < 0.22 ? 1 + 0.32 * (lf / 0.22) : 1 + 0.32 * (1 - EXPO((lf - 0.22) / 0.78));
    return { m, deck: t < T_LICHT ? 0 : p };
  };
  const bodenLage = (t: number) => {
    const p = EXPO(klemme((t - (T_STEIGEN + 0.52)) / 0.6, 0, 1));
    let deck = p;
    let sx = mix(0.6, 1, p);
    const h = (t - tHuepfen) / 0.62;
    if (h > 0 && h < 1) {
      const k = h < 0.45 ? SANFT(h / 0.45) : 1 - SANFT((h - 0.45) / 0.55);
      deck *= 1 - 0.55 * k;
      sx *= 1 - 0.28 * k;
    }
    return { deck, sx };
  };
  const funkeAuf = (t: number) => {
    const lt = t - tFunke;
    if (lt <= 0) return 0;
    return lt < 0.12 ? EXPO(lt / 0.12) : 1 - BUEHNE.blitz(lt - 0.12);
  };
  spur(spuren, 0, tEnde, hz50, (t) => {
    const gl = gaistLage(t);
    const l = leib(t);
    const a = auraLage(t);
    const bo = bodenLage(t);
    const bl = blick(t);
    const f = funkeAuf(t);
    const auge = { transform: `scaleY(${lid(t).toFixed(3)})` };
    return {
      fi: { transform: `translate(${(gl.p[0] - heim[0]).toFixed(2)}px, ${(gl.p[1] - heim[1]).toFixed(2)}px) scale(${gl.mass.toFixed(4)})` },
      sp: { transform: `perspective(${(g * 4.8).toFixed(0)}px) rotateY(${l.ry.toFixed(2)}deg) rotate(${l.rz.toFixed(2)}deg) scale(${l.sx.toFixed(4)}, ${l.sy.toFixed(4)})`, opacity: l.o },
      au: { transform: `scale(${a.m.toFixed(4)})`, opacity: a.deck },
      bo: { transform: `scaleX(${bo.sx.toFixed(4)})`, opacity: bo.deck },
      bl: { transform: `translate(${(bl[0] * 2.2).toFixed(3)}px, ${(bl[1] * 2.2).toFixed(3)}px)` },
      a0: auge,
      a1: auge,
      fu: { transform: `scale(${(1 + f * 1.2).toFixed(4)}) rotate(${(f * 24).toFixed(2)}deg)` },
      sc: { transform: `scale(${(1 + f * 0.9).toFixed(4)})`, opacity: mix(0.8, 1, f) },
    };
  }, { fi: figur, sp: sprung, au: aura, bo: boden, bl: blickG, a0: augen[0], a1: augen[1], fu: funke, sc: schein });
  // Strahlen hinter dem Leib: sechs Linien aus dem Funken
  if (sprung) {
    const strahlen = document.createElement("span");
    strahlen.className = "bue-strahlen";
    strahlen.setAttribute("aria-hidden", "true");
    sprung.prepend(strahlen);
    zusatz.push(strahlen);
    for (let i = 0; i < 6; i++) {
      const strahl = document.createElement("i");
      strahlen.append(strahl);
      const w = -90 + i * 60 + 18;
      einfach(spuren, strahl, [
        { transform: `rotate(${w}deg) translateX(${(6 * gf).toFixed(1)}px) scaleX(0.1)`, opacity: 0 },
        { transform: `rotate(${w}deg) translateX(${(10 * gf).toFixed(1)}px) scaleX(1)`, opacity: 1, offset: 0.35 },
        { transform: `rotate(${w}deg) translateX(${(26 * gf).toFixed(1)}px) scaleX(0.4)`, opacity: 0 },
      ], tFunke, 0.7, "cubic-bezier(0.16, 1, 0.3, 1)");
    }
  }
  // Lichtwelle vom Gaist über die Seite (iOS BuehneWelleBild, 1,5 s)
  {
    const welle = document.createElement("div");
    welle.className = "bue-welle";
    const groesse = g * 2.2;
    // W82 Web-11b W11B-1 (F-W11-1 Fassung A): am Desktop endet die Welle am Rand der Kacheln; ein Rahmen um Gaist und Kachelfläche
    // schneidet sie dort ab (der Browser zeichnet dann je Bild nur diesen Bereich neu statt des ganzen Fensters)
    const rahmen = mobil ? null : { links: Math.min(kb.links, gr.links), oben: Math.min(kb.oben, gr.oben), rechts: Math.max(kb.rechts, gr.rechts), unten: Math.max(kb.unten, gr.unten) };
    let halter: HTMLElement = vorn;
    if (rahmen) {
      halter = document.createElement("div");
      halter.className = "bue-welle-rahmen";
      halter.style.left = `${rahmen.links.toFixed(1)}px`;
      halter.style.top = `${rahmen.oben.toFixed(1)}px`;
      halter.style.width = `${(rahmen.rechts - rahmen.links).toFixed(1)}px`;
      halter.style.height = `${(rahmen.unten - rahmen.oben).toFixed(1)}px`;
      vorn.append(halter);
    }
    welle.style.width = `${groesse.toFixed(1)}px`;
    welle.style.height = `${groesse.toFixed(1)}px`;
    welle.style.left = `${(heim[0] - groesse / 2 - (rahmen?.links ?? 0)).toFixed(1)}px`;
    welle.style.top = `${(heim[1] - groesse / 2 - (rahmen?.oben ?? 0)).toFixed(1)}px`;
    halter.append(welle);
    const ende = welleEnde(W, H, groesse, heim, rahmen);
    const t0 = tFunke + 0.04;
    spur(spuren, t0, t0 + 1.5, hz50, (t) => {
      const p = klemme((t - t0) / 1.5, 0, 1);
      const deck = p < 0.08 ? (0.85 * p) / 0.08 : 0.85 * (1 - Math.pow((p - 0.08) / 0.92, 1.6));
      return { w: { transform: `scale(${(0.4 + (ende - 0.4) * WEG_WELLE(p)).toFixed(4)})`, opacity: t >= t0 + 1.5 ? 0 : deck } };
    }, { w: welle });
  }

  // ---------------------------------------------------------------- Kacheln: entstehen mit der Welle, Anstoßen, Licht
  const landungen: Record<Zelle, number[]> = { ueberfaellig: [], bald: [], pruefen: [], fixkosten: [] };
  if (held) landungen.pruefen.push(tHeldLand);
  for (const h of haufen) landungen[h.ziel].push(h.tLand);
  ZELLEN.forEach((z, i) => {
    const k = kacheln[z];
    if (!k) return;
    const tE = Math.min(welleBei(mitte(z)), tSortieren - 0.12) + i * 0.01;
    const stoesse = landungen[z].slice().sort((a, b) => a - b);
    const licht = k.querySelector<HTMLElement>(".k-licht");
    const basis = licht ? Number(getComputedStyle(licht).opacity) || 0 : 0;
    const ende = Math.max(tE + 0.9, ...stoesse.map((t) => t + 0.7));
    spur(spuren, 0, ende, hz50, (t) => {
      const lt = t - tE;
      const p = lt <= 0 ? 0 : KACHEL(lt);
      const o = lt <= 0 ? 0 : EXPO(klemme(lt / 0.26, 0, 1));
      const st = stoesse.reduce((s, tl) => s + stoss(t - tl, 0.095), 0);
      const s = mix(0.92, 1, p) * (1 + 0.034 * Math.min(1.4, st));
      const pl = stoesse.reduce((s, tl) => s + puls(t - tl, 0.12, 0.58), 0);
      return {
        // vor ihrem Takt steht die Kachel unsichtbar ohne Maßstab (Layout wie im Ruhebild); der Sprung auf 0,92 fällt in Deckkraft 0
        k: lt <= 0 ? { transform: "translateY(0px) scale(1)", opacity: 0 } : { transform: `translateY(${mix(12, 0, p).toFixed(2)}px) scale(${s.toFixed(4)})`, opacity: o },
        l: { opacity: Math.min(1, basis + (1 - basis) * Math.min(1, pl)) },
      };
    }, { k, l: licht });
    anstehend.push({ t: stoesse[0] ?? tE + 0.12, fn: () => ereignis.landung(z) });
  });

  // ---------------------------------------------------------------- Module: warten bis zu ihrem Takt, dann schweben sie ein
  // Module: die Abschnitte unter dem Kopf mit ihren Titeln (Fristen, Aufgaben, Bitte prüfen, Neue Post, Hinweise), in Lesefolge
  const module = Array.from(wurzel.querySelectorAll<HTMLElement>(".startseite-raster > * > *, main > :not(h1, .startseite-kopf, .startseite-raster)"))
    .map((el) => ({ el, r: el.getBoundingClientRect() }))
    .sort((a, b) => a.r.top - b.r.top || a.r.left - b.r.left)
    .map((x) => x.el);
  module.forEach((m, i) => {
    einfach(spuren, m, [{ transform: "translateY(26px)", opacity: 0 }, { transform: "none", opacity: 1 }], tModule + i * 0.11, 0.8, "cubic-bezier(0.16, 1, 0.3, 1)");
  });
  anstehend.push({ t: tModule, fn: () => ereignis.module() });
  anstehend.push({ t: tBlase, fn: () => ereignis.blase() });
  anstehend.push({ t: tEnde, fn: endeNormal });
  anstehend.sort((a, b) => a.t - b.t);

  // ---------------------------------------------------------------- Abspielen unter der Bühnenuhr
  const setzen = (t: number) => {
    for (const s of spuren) s.a.currentTime = (t - s.t0) * 1000;
  };
  const feuern = (t: number) => {
    for (let e = anstehend[0]; e && e.t <= t && !vorbei; e = anstehend[0]) {
      anstehend.shift();
      e.fn();
    }
  };
  const halt = haltBei();
  if (halt !== null) {
    zeit = halt;
    setzen(zeit);
    // nur die Ereignisse bis zur Haltezeit; das Ende kommt nicht (die Prüfhilfe hält das Bild)
    for (let e = anstehend[0]; e && e.t <= zeit && e.fn !== endeNormal; e = anstehend[0]) {
      anstehend.shift();
      e.fn();
    }
    return { schneiden, abbrechen };
  }
  setzen(0);
  const takt = (jetzt: number) => {
    if (vorbei) return;
    if (letzte !== null) {
      const dt = (jetzt - letzte) / 1000;
      const schritt = uhrTakt(dt, vorrat, verborgen, document.visibilityState !== "hidden");
      vorrat = schritt.vorrat;
      verborgen = schritt.verborgen;
      zeit += schritt.dt;
    }
    letzte = jetzt;
    setzen(zeit);
    feuern(zeit);
    if (!vorbei) rahmen = requestAnimationFrame(takt);
  };
  anmelden();
  rahmen = requestAnimationFrame(takt);
  return { schneiden, abbrechen };
}
